import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Assessment,
  FilterList,
  Close as CloseIcon,
  Refresh,
  Gavel,
  Sick,
  AccessTime,
  EventBusy,
  PictureAsPdf,
  TableChart,
  Person,
  Description,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isWithinInterval, parseISO } from 'date-fns';

// --- EXPORT LIBRARIES ---
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- ASSETS ---
import marabesLogo from '../assets/images/logos/Marabes.png';

// --- SERVICES ---
import { getEmployeeAbsences } from '../services/absenceAPIs';
import { getEmployeeNotes } from '../services/NoteAPIs';
import { getEmployeeImage, getAllEmployees } from '../services/HRstaff';
import { getAbsenceViolationByAbsenceId } from '../services/absenceviolationAPIs';
import PageHeader from '../components/headers/Header';
import { captureClientError } from '../utils/errorReporting';

// --- CONFIGURATION ---
if (typeof jsPDF.prototype.autoTable !== 'function') {
  jsPDF.prototype.autoTable = autoTable;
}

// === THEME ===
const THEME = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  primary: '#1E293B',
  secondary: '#64748B',
  accent: '#3B82F6',
  border: '#E2E8F0',
  tableHeader: '#F1F5F9',
  danger: '#EF4444',
  warning: '#F59E0B',
  success: '#10B981',
  info: '#3B82F6',
};

// === STYLES ===
const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: THEME.bg,
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  headerCard: {
    backgroundColor: THEME.surface,
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
    gap: '16px'
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleText: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: THEME.primary,
  },
  subTitle: {
    fontSize: '0.8rem',
    color: THEME.secondary,
  },
  controls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  tableCard: {
    backgroundColor: THEME.surface,
    borderRadius: '12px',
    border: `1px solid ${THEME.border}`,
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  th: {
    backgroundColor: THEME.tableHeader,
    color: THEME.primary,
    fontWeight: '700',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    padding: '12px 10px',
    borderBottom: `1px solid ${THEME.border}`,
    whiteSpace: 'nowrap',
    zIndex: 10,
    cursor: 'pointer',
    userSelect: 'none'
  },
  td: {
    padding: '10px 10px',
    color: '#334155',
    fontSize: '0.85rem',
    borderBottom: `1px solid ${THEME.border}`,
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  rowHover: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#F1F5F9',
    },
  },
  statBadge: {
    fontWeight: '700',
    borderRadius: '6px',
    fontSize: '0.75rem',
    height: '24px',
    minWidth: '28px'
  },
  exportBtn: {
    borderColor: THEME.border,
    color: THEME.secondary,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '6px 12px',
    '&:hover': {
      borderColor: THEME.accent,
      color: THEME.accent,
      backgroundColor: '#EFF6FF'
    }
  },
  modalHeader: {
    background: `linear-gradient(135deg, ${THEME.primary} 0%, #334155 100%)`,
    color: 'white',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  richHtmlContent: {
    fontSize: '0.85rem',
    lineHeight: 1.5,
    color: '#334155',
    '& p': { margin: '0 0 6px 0' },
    '& p:last-child': { margin: 0 },
    '& ul, & ol': { margin: '2px 0 6px 16px', paddingLeft: '8px' },
    '& li': { margin: '2px 0' },
    '& b, & strong': { fontWeight: 700, color: '#1E293B' },
    '& em, & i': { fontStyle: 'italic' },
    '& h1, & h2, & h3, & h4': { margin: '8px 0 4px 0', color: '#0F172A', fontWeight: 700, fontSize: '0.95rem' },
    '& br': { display: 'block', content: '""', marginTop: '2px' }
  }
};

// === HTML & ASSET HELPERS ===
const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

const stripHtml = (html) => {
  if (!html) return '';
  if (typeof html !== 'string') return String(html);
  const processed = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<\/li>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  const tmp = document.createElement('div');
  tmp.innerHTML = processed;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

// === HTML CONTENT DISPLAY COMPONENT ===
const HtmlContentDisplay = ({ content, fallback = '-', maxLines = null }) => {
  if (!content || !stripHtml(content).trim()) {
    return (
      <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
        {fallback}
      </Typography>
    );
  }

  if (isHtml(content)) {
    return (
      <Box
        sx={{
          ...styles.richHtmlContent,
          ...(maxLines ? {
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : {})
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.85rem',
        color: '#334155',
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
        ...(maxLines ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        } : {})
      }}
    >
      {content}
    </Typography>
  );
};

// === HELPERS ===
const formatDate = (date) => {
  if (!date) return 'N/A';
  return format(new Date(date), 'dd-MM-yyyy');
};

const getViolationConfig = (type) => {
  const normalized = type?.toUpperCase();
  if (normalized === 'LATENESS') return { label: 'Late', color: 'warning', icon: <AccessTime fontSize="small" /> };
  if (normalized === 'SICKNESS') return { label: 'Sick', color: 'error', icon: <Sick fontSize="small" /> };
  if (['PERSONAL', 'PERSONAL_COMMITMENT'].includes(normalized)) return { label: 'Personal', color: 'info', icon: <EventBusy fontSize="small" /> };
  if (normalized === 'FEEDBACK_NOTE' || normalized === 'NOTE') return { label: 'Note', color: 'default', icon: <Description fontSize="small" /> };
  if (normalized?.includes('VIOLATION') || normalized?.includes('SAFETY')) return { label: 'Violation', color: 'error', icon: <Gavel fontSize="small" /> };
  return { label: type || 'Other', color: 'default', icon: <Gavel fontSize="small" /> };
};

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      console.warn("Failed to load image:", url);
      resolve(null);
    };
  });
};

const parseNoteContent = (content) => {
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        isStructured: true,
        violationType: parsed.violationType || 'General Note',
        description: parsed.description || '',
        managerName: parsed.managerName || '',
        employeeReaction: parsed.employeeReaction || '',
        employeeAgreement: parsed.employeeAgreement || '',
        noteType: parsed.noteType || 'NEGATIVE'
      };
    }
    return { isStructured: false, description: content };
  } catch (e) {
    return { isStructured: false, description: content };
  }
};

// === VIOLATION COLUMNS (General Report) ===
const VIOLATION_COLUMNS = [
  { key: 'VIOLATION_OF_RULES', label: 'Violation of Rules' },
  { key: 'BEHAVIOR', label: 'Behavior' },
  { key: 'WORK_PERFORMANCE', label: 'Work Performance' },
  { key: 'BREAK_COMPLIANCE', label: 'Break Compliance' },
  { key: 'UNAUTHORIZED_ABSENCE', label: 'Unauthorized Absence' },
  { key: 'EARLY_DEPARTURE', label: 'Early Departure' },
  { key: 'INSUBORDINATION', label: 'Insubordination' },
  { key: 'HARASSMENT_OR_DISCRIMINATION', label: 'Harassment / Discrimination' },
  { key: 'USE_OF_MOBILE_PHONE_DURING_WORK', label: 'Mobile Phone Use' },
  { key: 'DRESS_CODE_VIOLATION', label: 'Dress Code Violation' },
  { key: 'UNPROFESSIONAL_COMMUNICATION', label: 'Unprofessional Comm.' },
  { key: 'DISRUPTION_OF_WORKPLACE', label: 'Disruption of Workplace' },
  { key: 'CONFIDENTIALITY_BREACH', label: 'Confidentiality Breach' },
];

const normalizeViolationType = (type) => {
  if (!type) return '';
  return type.toUpperCase().trim().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
};

const inRange = (dateStr, startDate, endDate) => {
  if (!dateStr || !startDate || !endDate) return false;
  try {
    const dStr = typeof dateStr === 'string' ? dateStr.substring(0, 10) : format(new Date(dateStr), 'yyyy-MM-dd');
    const startStr = typeof startDate === 'string' ? startDate.substring(0, 10) : format(new Date(startDate), 'yyyy-MM-dd');
    const endStr = typeof endDate === 'string' ? endDate.substring(0, 10) : format(new Date(endDate), 'yyyy-MM-dd');
    return dStr >= startStr && dStr <= endStr;
  } catch {
    return false;
  }
};

// === EMPLOYEE HISTORY MODAL ===
// startDate/endDate optional props for period-based filtering (General Report tab)
const EmployeeHistoryModal = ({ open, onClose, employee, startDate = null, endDate = null }) => {
  const isPeriodMode = !!(startDate && endDate);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [empImage, setEmpImage] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      if (employee?.id) {
        try {
          const imgBlob = await getEmployeeImage(employee.id);
          setEmpImage(imgBlob);
        } catch (e) {
          setEmpImage(null);
        }
      }
    };
    if (open) {
      setEmpImage(null);
      fetchImage();
    }
  }, [open, employee]);

  useEffect(() => {
    if (open && employee) fetchEmployeeHistory();
  }, [open, employee, selectedYear]);

  const fetchEmployeeHistory = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      // In period mode (General Report), fetch all absences; otherwise fetch by selected year
      const yearToFetch = isPeriodMode ? null : selectedYear;
      const absences = await getEmployeeAbsences(employee.id, yearToFetch);
      const notes = await getEmployeeNotes(employee.id);

      const relevantAbsences = absences.filter(a => {
        const typeOk = ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase());
        if (!typeOk) return false;
        if (isPeriodMode) return inRange(a.startDate || a.date, startDate, endDate);
        return true;
      });

      // Fetch violations for each absence in parallel
      // Skip absence-violation detail fetches in period mode (General Report) — 
      // those absences usually have no violation record (return 400) and the columns aren't shown there
      const absenceViolations = isPeriodMode
        ? relevantAbsences.map(() => null)
        : await Promise.all(
          relevantAbsences.map(async (a) => {
            const absId = a.id || a.requestId;
            if (!absId) return null;
            try { return await getAbsenceViolationByAbsenceId(absId); } catch { return null; }
          })
        );

      const processedAbsences = relevantAbsences.map((a, i) => {
        const violation = absenceViolations[i];
        return {
          id: a.id || a.requestId,
          date: new Date(a.startDate || a.date),
          type: a.ty_pe?.toUpperCase(),
          description: a.reason || a.justification || 'No description',
          recordedBy: a.createdBy || '-',
          managerReview: violation?.managerReview || null,
          employeeReaction: violation?.employeeReaction || null,
          employeeAgreement: violation?.employeeAgreement || null,
          hasViolation: !!violation,
          source: 'ABSENCE'
        };
      });

      const processedNotes = notes
        .filter(n => {
          if (isPeriodMode) return inRange(n.incidentDate || n.createdAt, startDate, endDate);
          return new Date(n.incidentDate || n.createdAt).getFullYear() === selectedYear;
        })
        .map(n => {
          let details = {};
          if (n.incidentDate) {
            details = {
              type: n.violationType || 'FEEDBACK_NOTE',
              desc: n.description,
              mgr: n.managerReview,
              react: n.employeeReaction,
              agree: n.employeeAgreement,
              noteType: n.noteType || 'NEGATIVE'
            };
          } else {
            const parsed = parseNoteContent(n.content);
            details = {
              type: parsed.isStructured ? parsed.violationType : 'FEEDBACK_NOTE',
              desc: parsed.description,
              mgr: parsed.managerName,
              react: parsed.employeeReaction,
              agree: parsed.employeeAgreement,
              noteType: parsed.noteType || 'NEGATIVE'
            };
          }
          return {
            id: n.id,
            date: new Date(n.incidentDate || n.createdAt),
            type: details.type,
            description: details.desc,
            managerReview: details.mgr,
            recordedBy: n.createdBy || '-',
            employeeReaction: details.react,
            employeeAgreement: details.agree,
            noteType: details.noteType,
            source: 'NOTE'
          };
        });

      const combined = [...processedAbsences, ...processedNotes].sort((a, b) => b.date - a.date);
      setHistoryData(combined);
    } catch (err) {
      console.error("Failed to fetch history", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = useMemo(() => {
    if (isPeriodMode || selectedMonth === 'All') return historyData;
    return historyData.filter(item => item.date.getMonth() === parseInt(selectedMonth));
  }, [historyData, selectedMonth, isPeriodMode]);

  const exportModalPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 14;
      const axoLogoUrl = '/cLogo.png';
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl(axoLogoUrl)
      ]);

      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, 30, 10);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', doc.internal.pageSize.width - margin - 30, 10, 30, 10);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(`History Report: ${employee.name}`, margin, 30);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const periodLabel = isPeriodMode ? `${formatDate(startDate)}to ${formatDate(endDate)}` : `Year: ${selectedYear}`;
      doc.text(periodLabel, margin, 36);

      const tableRows = filteredHistory.map(item => [
        formatDate(item.date),
        item.source === 'NOTE' ? 'Note' : getViolationConfig(item.type).label,
        stripHtml(item.description) || '-',
        stripHtml(item.managerReview) || '-',
        stripHtml(item.employeeReaction) || '-',
        stripHtml(item.employeeAgreement) || '-'
      ]);

      (doc.autoTable || autoTable)(doc, {
        startY: 42,
        head: [['Date', 'Type', 'Description', 'Manager feedback', 'Reaction', 'Agreement']],
        body: tableRows,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [30, 41, 59] },
        columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 25 }, 2: { cellWidth: 'auto' } }
      });

      doc.save(`${employee.name.replace(/\s+/g, '_')}_History.pdf`);
    } catch (e) {
      alert("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', height: '80vh' } }}>
      <Box sx={styles.modalHeader}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src={empImage}
            sx={{ width: 50, height: 50, border: '2px solid white', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '1.2rem' }}
          >
            {employee.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="700" sx={{ color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
              {employee.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 500 }}>
              {employee.position}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2, bgcolor: THEME.bg, borderBottom: `1px solid ${THEME.border}`, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {isPeriodMode ? (
          <Typography variant="body2" fontWeight={600} sx={{ color: THEME.secondary }}>
            Period: {formatDate(startDate)} to {formatDate(endDate)}
          </Typography>
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 100, bgcolor: 'background.paper', borderRadius: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                <MenuItem value={new Date().getFullYear()}>{new Date().getFullYear()}</MenuItem>
                <MenuItem value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'background.paper', borderRadius: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                <MenuItem value="All">All Months</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i} value={i.toString()}>{format(new Date(2000, i, 1), 'MMMM')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        <Box flex={1} />
        <Button variant="contained" size="small" startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />} onClick={exportModalPDF} disabled={exporting}>Export History</Button>
      </Box>

      <DialogContent sx={{ padding: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>
        ) : filteredHistory.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" sx={{ opacity: 0.5, p: 4 }}>
            <Assessment sx={{ fontSize: 60, mb: 2 }} />
            <Typography>No records found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Manager Review</TableCell>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Reaction</TableCell>
                  <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 'bold' }}>Agreement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell>
                      <Chip label={getViolationConfig(item.type).label} color={item.noteType === 'POSITIVE' ? 'success' : getViolationConfig(item.type).color} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal' }}>
                      <HtmlContentDisplay content={item.description} fallback="No description" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal' }}>
                      <HtmlContentDisplay content={item.managerReview} fallback="-" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal' }}>
                      <HtmlContentDisplay content={item.employeeReaction} fallback="-" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'normal' }}>
                      <HtmlContentDisplay content={item.employeeAgreement} fallback="-" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

// === GENERAL REPORT COMPONENT ===
const GeneralReport = () => {
  const now = new Date();
  const [quickFilter, setQuickFilter] = useState('THIS_MONTH');
  const [genStartDate, setGenStartDate] = useState(startOfMonth(now));
  const [genEndDate, setGenEndDate] = useState(endOfMonth(now));
  const [genData, setGenData] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genExporting, setGenExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [officeFilter, setOfficeFilter] = useState('ALL');
  const [violationsFilter, setViolationsFilter] = useState('All');

  const applyQuickFilter = (filter) => {
    const n = new Date();
    setQuickFilter(filter);
    if (filter === 'THIS_MONTH') { setGenStartDate(startOfMonth(n)); setGenEndDate(endOfMonth(n)); }
    else if (filter === 'THIS_QUARTER') { setGenStartDate(startOfQuarter(n)); setGenEndDate(endOfQuarter(n)); }
    else if (filter === 'ALL_YEAR') { setGenStartDate(startOfYear(n)); setGenEndDate(endOfYear(n)); }
  };

  const fetchGeneralReport = async () => {
    setGenLoading(true);
    setGenData([]);
    console.group('📊 [General Report Fetch]');
    console.log('Fetching general feedback report for period:', {
      startDate: format(genStartDate, 'dd-MM-yyyy'),
      endDate: format(genEndDate, 'dd-MM-yyyy')
    });

    try {
      const allEmployees = await getAllEmployees();
      console.log(`Loaded ${allEmployees?.length || 0} employees from API`);
      const currentYear = new Date().getFullYear();
      const sYear = genStartDate.getFullYear();
      const eYear = genEndDate.getFullYear();

      // Only fetch relevant years covered by period + current year
      const yearsToFetch = new Set();
      for (let y = sYear; y <= eYear; y++) yearsToFetch.add(y);
      yearsToFetch.add(currentYear);
      const yearsList = [...yearsToFetch];

      const BATCH_SIZE = 12;
      const allResults = [];

      for (let i = 0; i < allEmployees.length; i += BATCH_SIZE) {
        const batch = allEmployees.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (emp) => {
          const empId = emp.employeeId || emp.id;
          if (!empId) return null;
          try {
            const [absencesPerYear, notes] = await Promise.all([
              Promise.all(yearsList.map(y => getEmployeeAbsences(empId, y).catch(() => []))),
              getEmployeeNotes(empId).catch(() => [])
            ]);
            const absences = (absencesPerYear || []).flat();

            const filteredNotes = (notes || []).filter(n => inRange(n.incidentDate || n.createdAt, genStartDate, genEndDate));

            const currentYearStart = new Date(currentYear, 0, 1);
            const currentYearEnd = new Date(currentYear, 11, 31);
            const currentYearAbsences = absences.filter(a =>
              inRange(a.startDate || a.date, currentYearStart, currentYearEnd)
              && ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())
            );

            const vCounts = {};
            VIOLATION_COLUMNS.forEach(col => { vCounts[col.key] = 0; });
            filteredNotes.forEach(n => {
              let vType = n.violationType;
              if (!vType && n.content) {
                const p = parseNoteContent(n.content);
                vType = p.isStructured ? p.violationType : null;
              }
              if (vType) {
                const norm = normalizeViolationType(vType);
                if (Object.prototype.hasOwnProperty.call(vCounts, norm)) vCounts[norm]++;
              }
            });

            const totalViolations = Object.values(vCounts).reduce((a, b) => a + b, 0);

            return {
              employee: emp,
              empId,
              violations: vCounts,
              totalViolations,
              sick: currentYearAbsences.filter(a => a.ty_pe?.toUpperCase() === 'SICKNESS').length,
              late: currentYearAbsences.filter(a => a.ty_pe?.toUpperCase() === 'LATENESS').length,
              personal: currentYearAbsences.filter(a => ['PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())).length,
            };
          } catch (empErr) {
            console.error(`[GeneralReport] Error processing employee ${emp.firstName} ${emp.lastName}:`, empErr);
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults.filter(Boolean));
      }

      console.log(`General Report processed successfully. Total rows: ${allResults.length}`);
      console.groupEnd();
      setGenData(allResults);
    } catch (e) {
      console.error('[GeneralReport] Fatal error in General Report fetch:', e);
      console.groupEnd();
      captureClientError(e, { source: 'GeneralReport', page: window.location.pathname });
    } finally {
      setGenLoading(false);
    }
  };

  useEffect(() => { fetchGeneralReport(); }, [genStartDate, genEndDate]);

  // Derive office list from fetched data
  const officeList = useMemo(() => {
    const offices = [...new Set(genData.map(r => r.employee?.office).filter(Boolean))].sort();
    return offices;
  }, [genData]);

  // Apply office filter
  const filteredGenData = useMemo(() => {
    let data = genData;
    if (officeFilter !== 'ALL') {
      data = data.filter(r => r.employee?.office === officeFilter);
    }
    if (violationsFilter === 'Has Violations') {
      data = data.filter(r => r.totalViolations > 0);
    } else if (violationsFilter === 'No Violations') {
      data = data.filter(r => r.totalViolations === 0);
    }
    return data;
  }, [genData, officeFilter, violationsFilter]);

  // Sort state
  const [genSortKey, setGenSortKey] = useState('name');
  const [genSortDir, setGenSortDir] = useState('asc');

  const handleGenSort = (key) => {
    setGenSortDir(prev => key === genSortKey ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setGenSortKey(key);
  };

  const sortedGenData = useMemo(() => {
    const arr = [...filteredGenData];
    if (violationsFilter === 'Highest to Lowest') {
      arr.sort((a, b) => b.totalViolations - a.totalViolations);
    } else if (violationsFilter === 'Lowest to Highest') {
      arr.sort((a, b) => a.totalViolations - b.totalViolations);
    } else {
      arr.sort((a, b) => {
        let aVal, bVal;
        if (genSortKey === 'name') {
          aVal = `${a.employee?.firstName} ${a.employee?.lastName}`.toLowerCase();
          bVal = `${b.employee?.firstName} ${b.employee?.lastName}`.toLowerCase();
        } else if (genSortKey === 'sick') { aVal = a.sick; bVal = b.sick; }
        else if (genSortKey === 'late') { aVal = a.late; bVal = b.late; }
        else if (genSortKey === 'personal') { aVal = a.personal; bVal = b.personal; }
        else { aVal = a.violations[genSortKey] || 0; bVal = b.violations[genSortKey] || 0; }
        if (aVal < bVal) return genSortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return genSortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [filteredGenData, genSortKey, genSortDir, violationsFilter]);

  const SortIcon = ({ col }) => {
    if (genSortKey !== col) return <span style={{ opacity: 0.25, marginLeft: 2 }}>↕</span>;
    return <span style={{ marginLeft: 2, color: THEME.accent }}>{genSortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleEmpClick = (row) => {
    const emp = row.employee;
    const empId = emp?.employeeId || emp?.id;
    if (!empId) return;
    setSelectedEmployee({ name: `${emp.firstName} ${emp.lastName}`, id: empId });
    setModalOpen(true);
  };

  const CountBadge = ({ value }) => (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 28, height: 24, px: 0.5, borderRadius: '6px', fontWeight: 700, fontSize: '0.75rem',
      bgcolor: value >= 3 ? '#FEE2E2' : '#F1F5F9',
      color: value >= 3 ? '#DC2626' : '#64748B',
      border: value >= 3 ? '1px solid #FECACA' : '1px solid #E2E8F0'
    }}>{value}</Box>
  );

  const exportGenExcel = () => {
    if (!sortedGenData.length) return;
    const rows = sortedGenData.map(row => {
      const obj = { 'Employee Name': `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}` };
      VIOLATION_COLUMNS.forEach(col => { obj[col.label] = row.violations[col.key] || 0; });
      obj['Sick'] = row.sick; obj['Late'] = row.late; obj['Personal'] = row.personal;
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);

    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Style Header Row
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ c: C, r: 0 });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: "FF1E293B" } },
          font: { color: { rgb: "FFFFFFFF" }, bold: true },
          alignment: { horizontal: C === 0 ? "left" : "center", vertical: "center" }
        };
      }
    }

    // Style Data Rows
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
        const cell = ws[cellRef];
        if (cell) {
          if (C === 0) {
            // Employee Name column
            cell.s = {
              font: { bold: true },
              alignment: { horizontal: "left", vertical: "center" }
            };
          } else if (C <= VIOLATION_COLUMNS.length) {
            // Violation columns
            const val = Number(cell.v);
            if (val > 0) {
              cell.s = {
                fill: { fgColor: { rgb: "FFFEE2E2" } },
                font: { color: { rgb: "FF991B1B" }, bold: true },
                alignment: { horizontal: "center", vertical: "center" }
              };
            } else {
              cell.s = { alignment: { horizontal: "center", vertical: "center" } };
            }
          } else {
            // Sick, Late, Personal columns
            cell.s = { alignment: { horizontal: "center", vertical: "center" } };
          }
        }
      }
    }

    ws['!cols'] = [{ wch: 30 }]; // Make first column wider

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'General Report');
    XLSX.writeFile(wb, `General_Report_${format(genStartDate, 'dd-MM-yyyy')}_${format(genEndDate, 'dd-MM-yyyy')}.xlsx`);
  };

  const exportGenPDF = async () => {
    setGenExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 10; const pw = doc.internal.pageSize.width;
      const [mBase64, aBase64] = await Promise.all([getBase64FromUrl(marabesLogo), getBase64FromUrl('/logo1.png')]);
      if (mBase64) doc.addImage(mBase64, 'PNG', margin, 5, 25, 8);
      if (aBase64) doc.addImage(aBase64, 'PNG', pw - margin - 25, 5, 25, 8);
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
      doc.text('General Feedback Report', pw / 2, 13, { align: 'center' });
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${formatDate(genStartDate)} to ${formatDate(genEndDate)}`, margin, 22);
      const head = [['Employee', ...VIOLATION_COLUMNS.map(c => c.label), 'Sick', 'Late', 'Personal']];
      const body = sortedGenData.map(row => [
        `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`,
        ...VIOLATION_COLUMNS.map(col => row.violations[col.key] || 0),
        row.sick, row.late, row.personal
      ]);
      autoTable(doc, {
        startY: 26, head, body, theme: 'grid',
        styles: { fontSize: 7, halign: 'center' },
        headStyles: { fillColor: [30, 41, 59], fontSize: 6.5, halign: 'center' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 30 } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index > 0 && data.column.index <= VIOLATION_COLUMNS.length) {
            const val = Number(data.cell.raw);
            if (val > 0) { 
              data.cell.styles.fillColor = [254, 226, 226]; 
              data.cell.styles.textColor = [153, 27, 27]; 
              data.cell.styles.fontStyle = 'bold'; 
            }
          }
        }
      });
      doc.save(`General_Report_${format(genStartDate, 'dd-MM-yyyy')}_${format(genEndDate, 'dd-MM-yyyy')}.pdf`);
    } catch { alert('PDF export failed'); } finally { setGenExporting(false); }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Filters Bar */}
      <Box sx={{ bgcolor: THEME.surface, p: 2, borderRadius: '12px', border: `1px solid ${THEME.border}`, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Quick Filter</InputLabel>
          <Select value={quickFilter} label="Quick Filter" onChange={e => applyQuickFilter(e.target.value)}>
            <MenuItem value="THIS_MONTH">This Month</MenuItem>
            <MenuItem value="THIS_QUARTER">This Quarter</MenuItem>
            <MenuItem value="ALL_YEAR">All Year</MenuItem>
            {quickFilter === 'CUSTOM' && <MenuItem value="CUSTOM">Custom</MenuItem>}
          </Select>
        </FormControl>
        <DatePicker label="Start Date" value={genStartDate} onChange={v => { setGenStartDate(v); setQuickFilter('CUSTOM'); }} format="dd-MM-yyyy"
          slotProps={{ textField: { size: 'small', sx: { bgcolor: 'background.paper', width: 150 } } }} />
        <DatePicker label="End Date" value={genEndDate} onChange={v => { setGenEndDate(v); setQuickFilter('CUSTOM'); }} format="dd-MM-yyyy"
          slotProps={{ textField: { size: 'small', sx: { bgcolor: 'background.paper', width: 150 } } }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Office</InputLabel>
          <Select value={officeFilter} label="Office" onChange={e => setOfficeFilter(e.target.value)}>
            <MenuItem value="ALL">All Offices</MenuItem>
            {officeList.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Violations</InputLabel>
          <Select value={violationsFilter} label="Violations" onChange={e => setViolationsFilter(e.target.value)}>
            <MenuItem value="All">All Violations</MenuItem>
            <MenuItem value="Highest to Lowest">Highest to Lowest</MenuItem>
            <MenuItem value="Lowest to Highest">Lowest to Highest</MenuItem>
            <MenuItem value="Has Violations">Has Violations</MenuItem>
            <MenuItem value="No Violations">No Violations</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={fetchGeneralReport} startIcon={<Refresh />} sx={styles.exportBtn}>Refresh</Button>
        <Box flex={1} />
        <Button variant="outlined" onClick={exportGenExcel} disabled={genLoading || !sortedGenData.length} startIcon={<TableChart />} sx={styles.exportBtn}>Excel</Button>
        <Button variant="contained" onClick={exportGenPDF} disabled={genLoading || genExporting || !sortedGenData.length}
          startIcon={genExporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
          sx={{ bgcolor: THEME.primary }}>PDF</Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ ...styles.tableCard, overflowX: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
          <TableHead>
            <TableRow>
              <TableCell
                style={{ fontSize: '0.6rem' }}
                sx={{ ...styles.th, whiteSpace: 'normal', position: 'sticky', left: 0, zIndex: 20, minWidth: 120, bgcolor: THEME.tableHeader, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleGenSort('name')}
              >Employee Name<SortIcon col="name" /></TableCell>
              {VIOLATION_COLUMNS.map(col => (
                <TableCell key={col.key} align="center"
                  style={{ fontSize: '0.6rem' }}
                  sx={{ ...styles.th, whiteSpace: 'normal', minWidth: 60, lineHeight: 1.3, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleGenSort(col.key)}
                >{col.label}<SortIcon col={col.key} /></TableCell>
              ))}
              <TableCell align="center" style={{ fontSize: '0.6rem' }} sx={{ ...styles.th, whiteSpace: 'normal', minWidth: 44, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleGenSort('sick')}>Sick<SortIcon col="sick" /></TableCell>
              <TableCell align="center" style={{ fontSize: '0.6rem' }} sx={{ ...styles.th, whiteSpace: 'normal', minWidth: 44, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleGenSort('late')}>Late<SortIcon col="late" /></TableCell>
              <TableCell align="center" style={{ fontSize: '0.6rem' }} sx={{ ...styles.th, whiteSpace: 'normal', minWidth: 52, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleGenSort('personal')}>Personal<SortIcon col="personal" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {genLoading ? (
              <TableRow><TableCell colSpan={17} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
            ) : sortedGenData.length === 0 ? (
              <TableRow><TableCell colSpan={17} align="center" sx={{ py: 6 }}><Typography color="textSecondary">No records found for this period.</Typography></TableCell></TableRow>
            ) : sortedGenData.map((row, idx) => (
              <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                <TableCell
                  sx={{ ...styles.td, position: 'sticky', left: 0, bgcolor: 'white', zIndex: 5, fontWeight: 700, color: THEME.primary, minWidth: 140, cursor: 'pointer' }}
                  onClick={() => handleEmpClick(row)}
                >
                  <Tooltip title="Click to view history for this period" arrow>
                    <span style={{ textDecoration: 'underline dotted' }}>
                      {row.employee?.firstName} {row.employee?.lastName}
                    </span>
                  </Tooltip>
                </TableCell>
                {VIOLATION_COLUMNS.map(col => {
                  const val = row.violations[col.key] || 0;
                  return (
                    <TableCell 
                      key={col.key} 
                      align="center" 
                      sx={{ 
                        ...styles.td, 
                        bgcolor: val > 0 ? '#FEE2E2' : 'inherit',
                        color: val > 0 ? '#991B1B' : 'inherit',
                        fontWeight: val > 0 ? 'bold' : 'normal'
                      }}
                    >
                      {val}
                    </TableCell>
                  );
                })}
                <TableCell align="center" sx={styles.td}><Chip label={row.sick} size="small" sx={{ ...styles.statBadge, bgcolor: '#FEF2F2', color: '#DC2626' }} /></TableCell>
                <TableCell align="center" sx={styles.td}><Chip label={row.late} size="small" sx={{ ...styles.statBadge, bgcolor: '#FFF7ED', color: '#EA580C' }} /></TableCell>
                <TableCell align="center" sx={styles.td}><Chip label={row.personal} size="small" sx={{ ...styles.statBadge, bgcolor: '#EFF6FF', color: '#2563EB' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EmployeeHistoryModal
        open={modalOpen} onClose={() => setModalOpen(false)}
        employee={selectedEmployee} startDate={genStartDate} endDate={genEndDate}
      />
    </LocalizationProvider>
  );
};

// === RECORDS REPORT COMPONENT ===
const RecordsReport = () => {
  const now = new Date();
  const [quickFilter, setQuickFilter] = useState('THIS_MONTH');
  const [startDate, setStartDate] = useState(startOfMonth(now));
  const [endDate, setEndDate] = useState(endOfMonth(now));
  const [recordsData, setRecordsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filters
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [projectFilter, setProjectFilter] = useState('ALL');
  const [officeFilter, setOfficeFilter] = useState('ALL');

  // Sort
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const applyQuickFilter = (filter) => {
    const n = new Date();
    setQuickFilter(filter);
    if (filter === 'THIS_MONTH') { setStartDate(startOfMonth(n)); setEndDate(endOfMonth(n)); }
    else if (filter === 'THIS_QUARTER') { setStartDate(startOfQuarter(n)); setEndDate(endOfQuarter(n)); }
    else if (filter === 'ALL_YEAR') { setStartDate(startOfYear(n)); setEndDate(endOfYear(n)); }
  };

  const fetchRecordsReport = async () => {
    setLoading(true);
    setRecordsData([]);
    console.group('📋 [Records Report Fetch]');
    console.log('Fetching feedback records for period:', {
      startDate: format(startDate, 'dd-MM-yyyy'),
      endDate: format(endDate, 'dd-MM-yyyy')
    });

    try {
      const allEmployees = await getAllEmployees();
      console.log(`Loaded ${allEmployees?.length || 0} employees from API`);
      const currentYear = new Date().getFullYear();
      const sYear = startDate.getFullYear();
      const eYear = endDate.getFullYear();

      const yearsToFetch = new Set();
      for (let y = sYear; y <= eYear; y++) yearsToFetch.add(y);
      yearsToFetch.add(currentYear);
      const yearsList = [...yearsToFetch];

      const BATCH_SIZE = 12;
      const allResults = [];

      for (let i = 0; i < allEmployees.length; i += BATCH_SIZE) {
        const batch = allEmployees.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (emp) => {
          const empId = emp.employeeId || emp.id;
          if (!empId) return [];
          try {
            const [absencesPerYear, empNotes] = await Promise.all([
              Promise.all(yearsList.map(y => getEmployeeAbsences(empId, y).catch(() => []))),
              getEmployeeNotes(empId).catch(() => [])
            ]);
            const absences = (absencesPerYear || []).flat();

            const periodAbsences = absences.filter(a => {
              const typeOk = ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase());
              return typeOk && inRange(a.startDate || a.date, startDate, endDate);
            });

            const periodNotes = (empNotes || []).filter(n => inRange(n.incidentDate || n.createdAt, startDate, endDate));

            if (periodAbsences.length === 0 && periodNotes.length === 0) return [];

            const currentYearStart = new Date(currentYear, 0, 1);
            const currentYearEnd = new Date(currentYear, 11, 31);
            const currentYearAbsences = absences.filter(a =>
              inRange(a.startDate || a.date, currentYearStart, currentYearEnd)
              && ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())
            );
            const stats = {
              sick: currentYearAbsences.filter(a => a.ty_pe?.toUpperCase() === 'SICKNESS').length,
              late: currentYearAbsences.filter(a => a.ty_pe?.toUpperCase() === 'LATENESS').length,
              personal: currentYearAbsences.filter(a => ['PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())).length,
            };

            const violations = await Promise.all(
              periodAbsences.map(async (a) => {
                const absId = a.id || a.requestId;
                if (!absId) return null;
                try { return await getAbsenceViolationByAbsenceId(absId); } catch { return null; }
              })
            );

            const absenceRows = periodAbsences.map((a, i) => ({
              id: a.id || a.requestId,
              employee: emp,
              recordType: 'ABSENCE',
              date: a.startDate || a.date,
              ty_pe: a.ty_pe,
              reason: a.reason || a.justification || 'No description',
              noteDetails: {
                managerReview: violations[i]?.managerReview || null,
                employeeReaction: violations[i]?.employeeReaction || null,
                employeeAgreement: violations[i]?.employeeAgreement || null,
              },
              createdBy: a.createdBy || '-',
              stats,
            }));

            const noteRows = periodNotes.map(n => {
              let details = {};
              if (n.incidentDate) {
                details = { type: n.violationType || 'FEEDBACK_NOTE', desc: n.description, mgr: n.managerReview, react: n.employeeReaction, agree: n.employeeAgreement };
              } else {
                const parsed = parseNoteContent(n.content);
                details = { type: parsed.isStructured ? parsed.violationType : 'FEEDBACK_NOTE', desc: parsed.description, mgr: parsed.managerName, react: parsed.employeeReaction, agree: parsed.employeeAgreement };
              }
              return {
                id: n.id,
                employee: emp,
                recordType: 'NOTE',
                date: n.incidentDate || n.createdAt,
                ty_pe: details.type,
                reason: details.desc,
                noteDetails: { managerReview: details.mgr, employeeReaction: details.react, employeeAgreement: details.agree },
                createdBy: n.createdBy || '-',
                stats,
              };
            });

            return [...absenceRows, ...noteRows];
          } catch (empErr) {
            console.error(`[RecordsReport] Error processing employee ${emp.firstName} ${emp.lastName}:`, empErr);
            return [];
          }
        });

        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults.flat());
      }

      console.log(`Records Report processed successfully. Total records: ${allResults.length}`);
      console.groupEnd();
      setRecordsData(allResults);
    } catch (e) {
      console.error('[RecordsReport] Fatal error in Records Report fetch:', e);
      console.groupEnd();
      captureClientError(e, { source: 'RecordsReport', page: window.location.pathname });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecordsReport(); }, [startDate, endDate]);

  // Derive filter options from fetched data
  const deptList = useMemo(() => [...new Set(recordsData.map(r => r.employee?.department?.name).filter(Boolean))].sort(), [recordsData]);
  const projectList = useMemo(() => [...new Set(recordsData.map(r => r.employee?.projectName).filter(Boolean))].sort(), [recordsData]);
  const officeList = useMemo(() => [...new Set(recordsData.map(r => r.employee?.office?.name || r.employee?.office).filter(Boolean))].sort(), [recordsData]);

  // Apply filters client-side
  const filteredRecords = useMemo(() => {
    return recordsData.filter(r => {
      const deptOk = deptFilter === 'ALL' || r.employee?.department?.name === deptFilter;
      const projOk = projectFilter === 'ALL' || r.employee?.projectName === projectFilter;
      const officeVal = r.employee?.office?.name || r.employee?.office;
      const offOk = officeFilter === 'ALL' || officeVal === officeFilter;
      return deptOk && projOk && offOk;
    });
  }, [recordsData, deptFilter, projectFilter, officeFilter]);

  // Sorting
  const handleSort = (key) => setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));

  const sortedRecords = useMemo(() => {
    const arr = [...filteredRecords];
    arr.sort((a, b) => {
      let aVal = '', bVal = '';
      switch (sortConfig.key) {
        case 'name': aVal = `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.toLowerCase(); bVal = `${b.employee?.firstName || ''} ${b.employee?.lastName || ''}`.toLowerCase(); break;
        case 'date': aVal = a.date || ''; bVal = b.date || ''; break;
        case 'type': aVal = (a.ty_pe || '').toLowerCase(); bVal = (b.ty_pe || '').toLowerCase(); break;
        case 'sick': aVal = a.stats?.sick || 0; bVal = b.stats?.sick || 0; break;
        case 'late': aVal = a.stats?.late || 0; bVal = b.stats?.late || 0; break;
        case 'personal': aVal = a.stats?.personal || 0; bVal = b.stats?.personal || 0; break;
        default: return 0;
      }
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return arr;
  }, [filteredRecords, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" sx={{ opacity: 0.7, ml: 0.5 }} /> : <ArrowDownward fontSize="small" sx={{ opacity: 0.7, ml: 0.5 }} />;
  };

  const handleRowClick = (row) => {
    const emp = row.employee;
    const empId = emp?.employeeId || emp?.id;
    if (!empId) return;
    setSelectedEmployee({ name: `${emp.firstName} ${emp.lastName}`, id: empId });
    setModalOpen(true);
  };

  // Exports
  const exportExcel = () => {
    if (!filteredRecords.length) return;
    const rows = filteredRecords.map(row => ({
      'Employee Name': `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`,
      'Department': row.employee?.department?.name || 'N/A',
      'Project': row.employee?.projectName || 'N/A',
      'Office': row.employee?.office?.name || row.employee?.office || 'N/A',
      'Date': row.date ? formatDate(row.date) : '-',
      'Type': getViolationConfig(row.ty_pe).label,
      'Description': stripHtml(row.reason) || '-',
      'Manager Review': stripHtml(row.noteDetails?.managerReview) || '-',
      'Reaction': stripHtml(row.noteDetails?.employeeReaction) || '-',
      'Agreement': stripHtml(row.noteDetails?.employeeAgreement) || '-',
      'Sick (YTD)': row.stats?.sick || 0,
      'Late (YTD)': row.stats?.late || 0,
      'Personal (YTD)': row.stats?.personal || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feeback Records Report');
    XLSX.writeFile(wb, `Feeback_Records_Report_${format(startDate, 'dd-MM-yyyy')}_${format(endDate, 'dd-MM-yyyy')}.xlsx`);
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 10;
      const pw = doc.internal.pageSize.width;

      // --- Logos ---
      const [mBase64, aBase64] = await Promise.all([getBase64FromUrl(marabesLogo), getBase64FromUrl('/logo1.png')]);
      if (mBase64) doc.addImage(mBase64, 'PNG', margin, 6, 25, 8);
      if (aBase64) doc.addImage(aBase64, 'PNG', pw - margin - 25, 6, 25, 8);

      // --- Dynamic title ---
      const titleParts = ['Feedback Records Report'];
      if (deptFilter !== 'ALL') titleParts.push(`- ${deptFilter}`);
      if (projectFilter !== 'ALL') titleParts.push(`/ ${projectFilter}`);
      if (officeFilter !== 'ALL') titleParts.push(`/ ${officeFilter}`);
      const pdfTitle = titleParts.join(' ');

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(pdfTitle, pw / 2, 18, { align: 'center' });

      // --- Styled info band ---
      const bandY = 22;
      const bandH = 14;
      doc.setFillColor(241, 245, 249);  // light slate
      doc.roundedRect(margin, bandY, pw - margin * 2, bandH, 2, 2, 'F');

      // Period
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('Period:', margin + 3, bandY + 5.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.text(`${formatDate(startDate)}  to  ${formatDate(endDate)}`, margin + 17, bandY + 5.5);

      // Record count
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(59, 130, 246);
      doc.text('Records:', margin + 3, bandY + 11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.text(String(filteredRecords.length), margin + 18, bandY + 11);

      // Active filter labels (right side)
      const filterLabels = [];
      if (deptFilter !== 'ALL') filterLabels.push(`Dept: ${deptFilter}`);
      if (projectFilter !== 'ALL') filterLabels.push(`Project: ${projectFilter}`);
      if (officeFilter !== 'ALL') filterLabels.push(`Office: ${officeFilter}`);
      if (filterLabels.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(59, 130, 246);
        doc.text('Filters:', pw - margin - 90, bandY + 5.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        doc.text(filterLabels.join('   |   '), pw - margin - 73, bandY + 5.5);
      }

      // Generated timestamp (bottom-right of band)
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, pw - margin - 50, bandY + 11);

      // --- Table ---
      const body = filteredRecords.map(row => [
        `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`,
        row.date ? formatDate(row.date) : '-',
        getViolationConfig(row.ty_pe).label,
        stripHtml(row.reason) || '-',
        stripHtml(row.noteDetails?.managerReview) || '-',
        stripHtml(row.noteDetails?.employeeReaction) || '-',
        stripHtml(row.noteDetails?.employeeAgreement) || '-',
        row.stats?.sick || 0,
        row.stats?.late || 0,
        row.stats?.personal || 0,
      ]);

      autoTable(doc, {
        startY: bandY + bandH + 3,
        head: [['Name', 'Date', 'Type', 'Description', 'Manager Review', 'Reaction', 'Agreement', 'Sick', 'Late', 'Personal']],
        body,
        theme: 'grid',
        styles: { fontSize: 7, halign: 'center' },
        headStyles: { fillColor: [30, 41, 59], fontSize: 6.5, halign: 'center' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 28 }, 3: { halign: 'left', cellWidth: 40 } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index >= 7) {
            const val = Number(data.cell.raw);
            if (val >= 3) { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fontStyle = 'bold'; }
          }
        }
      });

      // File name includes active filters
      const fileSlug = [
        'Records_Report',
        format(startDate, 'dd-MM-yyyy'),
        format(endDate, 'dd-MM-yyyy'),
        deptFilter !== 'ALL' ? deptFilter.replace(/\s+/g, '_') : null,
        projectFilter !== 'ALL' ? projectFilter.replace(/\s+/g, '_') : null,
        officeFilter !== 'ALL' ? officeFilter.replace(/\s+/g, '_') : null,
      ].filter(Boolean).join('_');
      doc.save(`${fileSlug}.pdf`);
    } catch { alert('PDF export failed'); } finally { setExporting(false); }
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Filters Bar */}
      <Box sx={{ bgcolor: THEME.surface, p: 2, borderRadius: '12px', border: `1px solid ${THEME.border}`, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Quick Filter</InputLabel>
          <Select value={quickFilter} label="Quick Filter" onChange={e => applyQuickFilter(e.target.value)}>
            <MenuItem value="THIS_MONTH">This Month</MenuItem>
            <MenuItem value="THIS_QUARTER">This Quarter</MenuItem>
            <MenuItem value="ALL_YEAR">All Year</MenuItem>
            {quickFilter === 'CUSTOM' && <MenuItem value="CUSTOM">Custom</MenuItem>}
          </Select>
        </FormControl>
        <DatePicker label="Start Date" value={startDate} onChange={v => { setStartDate(v); setQuickFilter('CUSTOM'); }} format="dd-MM-yyyy"
          slotProps={{ textField: { size: 'small', sx: { bgcolor: 'background.paper', width: 150 } } }} />
        <DatePicker label="End Date" value={endDate} onChange={v => { setEndDate(v); setQuickFilter('CUSTOM'); }} format="dd-MM-yyyy"
          slotProps={{ textField: { size: 'small', sx: { bgcolor: 'background.paper', width: 150 } } }} />
        {/* Department filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Department</InputLabel>
          <Select value={deptFilter} label="Department" onChange={e => setDeptFilter(e.target.value)}>
            <MenuItem value="ALL">All Departments</MenuItem>
            {deptList.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Project filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select value={projectFilter} label="Project" onChange={e => setProjectFilter(e.target.value)}>
            <MenuItem value="ALL">All Projects</MenuItem>
            {projectList.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Office filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Office</InputLabel>
          <Select value={officeFilter} label="Office" onChange={e => setOfficeFilter(e.target.value)}>
            <MenuItem value="ALL">All Offices</MenuItem>
            {officeList.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={fetchRecordsReport} startIcon={<Refresh />} sx={styles.exportBtn}>Refresh</Button>
        <Box flex={1} />
        <Button variant="outlined" onClick={exportExcel} disabled={loading || !filteredRecords.length} startIcon={<TableChart />} sx={styles.exportBtn}>Excel</Button>
        <Button variant="contained" onClick={exportPDF} disabled={loading || exporting || !filteredRecords.length}
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
          sx={{ bgcolor: THEME.primary }}>PDF</Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ ...styles.tableCard, overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...styles.th, minWidth: 140, cursor: 'pointer' }} onClick={() => handleSort('name')}>
                <Box display="flex" alignItems="center">Name {getSortIcon('name')}</Box>
              </TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 95, cursor: 'pointer' }} onClick={() => handleSort('date')}>
                <Box display="flex" alignItems="center">Date {getSortIcon('date')}</Box>
              </TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 80, cursor: 'pointer' }} onClick={() => handleSort('type')}>
                <Box display="flex" alignItems="center">Type {getSortIcon('type')}</Box>
              </TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 200 }}>Description</TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 150 }}>Manager Review</TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 150 }}>Reaction</TableCell>
              <TableCell sx={{ ...styles.th, minWidth: 160 }}>Agreement</TableCell>
              <TableCell align="center" sx={{ ...styles.th, minWidth: 55, cursor: 'pointer' }} onClick={() => handleSort('sick')}>
                <Box display="flex" alignItems="center" justifyContent="center">Sick {getSortIcon('sick')}</Box>
              </TableCell>
              <TableCell align="center" sx={{ ...styles.th, minWidth: 55, cursor: 'pointer' }} onClick={() => handleSort('late')}>
                <Box display="flex" alignItems="center" justifyContent="center">Late {getSortIcon('late')}</Box>
              </TableCell>
              <TableCell align="center" sx={{ ...styles.th, minWidth: 70, cursor: 'pointer' }} onClick={() => handleSort('personal')}>
                <Box display="flex" alignItems="center" justifyContent="center">Personal {getSortIcon('personal')}</Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
            ) : sortedRecords.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><Typography color="textSecondary">No records found for this period.</Typography></TableCell></TableRow>
            ) : sortedRecords.map((row, idx) => (
              <TableRow key={idx} sx={styles.rowHover} onClick={() => handleRowClick(row)}>
                <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{row.employee?.firstName} {row.employee?.lastName}</TableCell>
                <TableCell sx={styles.td}>{row.date ? formatDate(row.date) : '-'}</TableCell>
                <TableCell sx={styles.td}>
                  <Chip label={getViolationConfig(row.ty_pe).label} color={getViolationConfig(row.ty_pe).color} size="small" variant="outlined" sx={{ fontWeight: 600 }} icon={getViolationConfig(row.ty_pe).icon} />
                </TableCell>
                <TableCell sx={styles.td}><Tooltip title={stripHtml(row.reason) || '-'} arrow><span>{stripHtml(row.reason) || '-'}</span></Tooltip></TableCell>
                <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.managerReview) || '-'} arrow><span>{stripHtml(row.noteDetails?.managerReview) || '-'}</span></Tooltip></TableCell>
                <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.employeeReaction) || '-'} arrow><span>{stripHtml(row.noteDetails?.employeeReaction) || '-'}</span></Tooltip></TableCell>
                <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.employeeAgreement) || '-'} arrow><span>{stripHtml(row.noteDetails?.employeeAgreement) || '-'}</span></Tooltip></TableCell>
                <TableCell align="center" sx={styles.td}><Chip label={row.stats?.sick || 0} sx={{ ...styles.statBadge, bgcolor: '#FEF2F2', color: '#DC2626' }} /></TableCell>
                <TableCell align="center" sx={styles.td}><Chip label={row.stats?.late || 0} sx={{ ...styles.statBadge, bgcolor: '#FFF7ED', color: '#EA580C' }} /></TableCell>
                <TableCell align="center" sx={styles.td}><Chip label={row.stats?.personal || 0} sx={{ ...styles.statBadge, bgcolor: '#EFF6FF', color: '#2563EB' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EmployeeHistoryModal
        open={modalOpen} onClose={() => setModalOpen(false)}
        employee={selectedEmployee} startDate={startDate} endDate={endDate}
      />
    </LocalizationProvider>
  );
};

// === MAIN COMPONENT ===
const EmployeeFeedbackReport = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Tab
  const [activeTab, setActiveTab] = useState(0);

  // Sort Config
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // --- DATA FETCHING ---
  const fetchReportData = async () => {
    setLoading(true);
    setReportData([]);
    console.group('📅 [Daily Report Fetch]');
    console.log('Fetching daily feedback report for date:', format(selectedDate, 'dd-MM-yyyy'));

    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const currentYear = selectedDate.getFullYear();

      const allEmployees = await getAllEmployees();
      console.log(`Loaded ${allEmployees?.length || 0} employees from API`);

      const BATCH_SIZE = 12;
      const allResults = [];

      for (let i = 0; i < allEmployees.length; i += BATCH_SIZE) {
        const batch = allEmployees.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (emp) => {
          const empId = emp.employeeId || emp.id;
          if (!empId) return [];

          try {
            const [absenceCurrent, empNotes] = await Promise.all([
              getEmployeeAbsences(empId, currentYear).catch(() => []),
              getEmployeeNotes(empId).catch(() => [])
            ]);

            const dateAbsences = (absenceCurrent || []).filter(r => {
              const rDate = r.startDate || r.date || '';
              const isTargetDate = rDate.startsWith(dateStr);
              const isTargetType = ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(r.ty_pe?.toUpperCase());
              return isTargetDate && isTargetType;
            });

            const dateNotes = (empNotes || []).filter(n => {
              const nDate = n.incidentDate || n.createdAt || '';
              return nDate.startsWith(dateStr);
            });

            if (dateAbsences.length === 0 && dateNotes.length === 0) return [];

            const relevantCurrentYear = (absenceCurrent || []).filter(h =>
              ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(h.ty_pe?.toUpperCase())
            );
            const stats = {
              sick: relevantCurrentYear.filter(h => h.ty_pe?.toUpperCase() === 'SICKNESS').length,
              late: relevantCurrentYear.filter(h => h.ty_pe?.toUpperCase() === 'LATENESS').length,
              personal: relevantCurrentYear.filter(h => ['PERSONAL', 'PERSONAL_COMMITMENT'].includes(h.ty_pe?.toUpperCase())).length
            };

            const todayViolations = await Promise.all(
              dateAbsences.map(async (r) => {
                const absId = r.id || r.requestId;
                if (!absId) return null;
                try { return await getAbsenceViolationByAbsenceId(absId); } catch { return null; }
              })
            );

            const todayAbsences = dateAbsences.map((r, i) => {
              const violation = todayViolations[i];
              return {
                ...r,
                employee: emp,
                recordType: 'ABSENCE',
                createdBy: r.createdBy || '-',
                noteDetails: {
                  managerReview: violation?.managerReview || null,
                  employeeReaction: violation?.employeeReaction || null,
                  employeeAgreement: violation?.employeeAgreement || null,
                },
                hasViolation: !!violation,
                stats
              };
            });

            const todayNotes = dateNotes.map(n => {
              let details = {};
              if (n.incidentDate) {
                details = {
                  type: n.violationType || 'FEEDBACK_NOTE',
                  desc: n.description,
                  mgr: n.managerReview,
                  react: n.employeeReaction,
                  agree: n.employeeAgreement
                };
              } else {
                const parsed = parseNoteContent(n.content);
                details = {
                  type: parsed.isStructured ? parsed.violationType : 'FEEDBACK_NOTE',
                  desc: parsed.description,
                  mgr: parsed.managerName,
                  react: parsed.employeeReaction,
                  agree: parsed.employeeAgreement
                };
              }
              return {
                id: n.id,
                employee: emp,
                recordType: 'NOTE',
                ty_pe: details.type,
                reason: details.desc,
                noteDetails: { managerReview: details.mgr, employeeReaction: details.react, employeeAgreement: details.agree },
                createdBy: n.createdBy || '-',
                stats
              };
            });

            return [...todayAbsences, ...todayNotes];
          } catch (err) {
            console.error(`[DailyReport] Error processing employee ${emp.firstName} ${emp.lastName}:`, err);
            return [];
          }
        });

        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults.flat());
      }

      console.log(`Daily Report processed successfully. Total records for today: ${allResults.length}`);
      console.groupEnd();
      setReportData(allResults);
    } catch (error) {
      console.error("[DailyReport] Fatal error in Daily Report fetch:", error);
      console.groupEnd();
      captureClientError(error, { source: 'DailyReport', page: window.location.pathname });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedDate]);

  const handleRowClick = (record) => {
    const emp = record.employee;
    const empId = emp?.employeeId || emp?.id;
    if (!empId) return;
    setSelectedEmployee({
      name: `${emp.firstName} ${emp.lastName}`,
      id: empId
    });
    setModalOpen(true);
  };

  // --- SORTING ---
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    let sortableData = [...reportData];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aVal = '', bVal = '';
        switch (sortConfig.key) {
          case 'name':
            aVal = `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.toLowerCase();
            bVal = `${b.employee?.firstName || ''} ${b.employee?.lastName || ''}`.toLowerCase();
            break;
          case 'violation': aVal = (a.ty_pe || '').toLowerCase(); bVal = (b.ty_pe || '').toLowerCase(); break;
          case 'description': aVal = stripHtml(a.reason || '').toLowerCase(); bVal = stripHtml(b.reason || '').toLowerCase(); break;
          case 'managerReview': aVal = stripHtml(a.noteDetails?.managerReview || '').toLowerCase(); bVal = stripHtml(b.noteDetails?.managerReview || '').toLowerCase(); break;
          case 'recordedBy': aVal = (a.createdBy || '').toLowerCase(); bVal = (b.createdBy || '').toLowerCase(); break;
          case 'reaction': aVal = stripHtml(a.noteDetails?.employeeReaction || '').toLowerCase(); bVal = stripHtml(b.noteDetails?.employeeReaction || '').toLowerCase(); break;
          case 'agreement': aVal = stripHtml(a.noteDetails?.employeeAgreement || '').toLowerCase(); bVal = stripHtml(b.noteDetails?.employeeAgreement || '').toLowerCase(); break;
          case 'sick': aVal = a.stats?.sick || 0; bVal = b.stats?.sick || 0; break;
          case 'late': aVal = a.stats?.late || 0; bVal = b.stats?.late || 0; break;
          case 'personal': aVal = a.stats?.personal || 0; bVal = b.stats?.personal || 0; break;
          default: return 0;
        }
        if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    return sortableData;
  }, [reportData, sortConfig]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" sx={{ opacity: 0.7, ml: 0.5 }} /> : <ArrowDownward fontSize="small" sx={{ opacity: 0.7, ml: 0.5 }} />;
  };

  // --- EXPORT ---
  const exportToExcel = () => {
    if (reportData.length === 0) return;
    const dataToExport = reportData.map(row => ({
      'Employee Name': `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`,
      'Date': format(selectedDate, 'dd-MM-yyyy'),
      'Type': getViolationConfig(row.ty_pe).label,
      'Description': stripHtml(row.reason) || 'N/A',
      'Manager Review': stripHtml(row.noteDetails?.managerReview) || '-',
      'Recorded By': row.createdBy || '-',
      'Employee Reaction': stripHtml(row.noteDetails?.employeeReaction) || '-',
      'Employee Agreement': stripHtml(row.noteDetails?.employeeAgreement) || '-',
      'Sick ': row.stats?.sick || 0,
      'Late ': row.stats?.late || 0,
      'Personal ': row.stats?.personal || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Feedback Report");
    XLSX.writeFile(wb, `Feedback_Report_${format(selectedDate, 'dd-MM-yyyy')}.xlsx`);
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 14;
      const pageWidth = doc.internal.pageSize.width;
      const axoLogoUrl = '/logo1.png';
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl(axoLogoUrl)
      ]);
      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, 30, 10);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', doc.internal.pageSize.width - margin - 30, 10, 30, 10);

      const addHeader = (pageNum) => {
        if (pageNum > 1) doc.addPage();
        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 25, 'F');
        try { doc.addImage(marabesLogo, 'PNG', margin, 7, 30, 10); } catch (e) { }
        if (axoBase64) doc.addImage(axoBase64, 'PNG', pageWidth - margin - 30, 7, 30, 10);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Employee Feedback Report', pageWidth / 2, 16, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Date: ${format(selectedDate, 'dd-MM-yyyy')}`, margin, 36);
      };

      addHeader(1);
      let y = 30;

      const tableRows = reportData.map(row => [
        `${row.employee?.firstName} ${row.employee?.lastName}`,
        getViolationConfig(row.ty_pe).label,
        stripHtml(row.reason) || 'N/A',
        stripHtml(row.noteDetails?.managerReview) || '-',
        stripHtml(row.noteDetails?.employeeReaction) || '-',
        stripHtml(row.noteDetails?.employeeAgreement) || '-',
        row.stats?.sick || 0,
        row.stats?.late || 0,
        row.stats?.personal || 0
      ]);

      autoTable(doc, {
        startY: 45,
        head: [['Name', 'Type', 'Description of Incident', 'Manager Review by…', 'Employee Reaction', 'Employee Agreement', 'Sick', 'Late', 'Personal']],
        body: tableRows,
        theme: 'grid',
        styles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 35, fontStyle: 'bold' }, 2: { cellWidth: 40 } }
      });
      doc.save(`Feedback_Report_${format(selectedDate, 'dd-MM-yyyy')}.pdf`);
    } catch (e) { alert("PDF Failed"); } finally { setExporting(false); }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={styles.container}>
        {/* Header */}
        <PageHeader
          title="Employee Feedback Report"
          subtitle="Incident, Violation & Absence Tracking"
          actions={
            activeTab === 0 ? (
              <Box sx={styles.controls}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: THEME.secondary, fontWeight: 600, fontSize: '0.85rem' }}>
                  <FilterList fontSize="small" /> Date:
                </Box>
                <DatePicker
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  format="dd-MM-yyyy"
                  slotProps={{ textField: { size: 'small', sx: { bgcolor: 'background.paper', width: 140, borderRadius: 1 } } }}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '30px', display: { xs: 'none', md: 'block' } }} />
                <Button variant="outlined" onClick={fetchReportData} startIcon={<Refresh />} sx={styles.exportBtn}>Refresh</Button>
                <Button variant="outlined" onClick={exportToExcel} disabled={loading || reportData.length === 0} startIcon={<TableChart />} sx={styles.exportBtn}>Excel</Button>
                <Button variant="contained" onClick={exportToPDF} disabled={loading || exporting || reportData.length === 0} startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />} sx={{ bgcolor: THEME.primary }}>PDF</Button>
              </Box>
            ) : null
          }
        />

        {/* Tabs */}
        <Box sx={{ bgcolor: THEME.surface, borderRadius: '12px', border: `1px solid ${THEME.border}`, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: `1px solid ${THEME.border}`,
              px: 2,
              '& .MuiTab-root': { fontWeight: 600, fontSize: '0.9rem', textTransform: 'none', minHeight: 48 },
              '& .Mui-selected': { color: THEME.accent },
              '& .MuiTabs-indicator': { backgroundColor: THEME.accent, height: 3, borderRadius: '3px 3px 0 0' }
            }}
          >
            <Tab label="Daily Report" />
            <Tab label="General Report" />
            <Tab label="Records Report" />
          </Tabs>
        </Box>

        {/* Tab 0— Daily Report */}
        {activeTab === 0 && (
          <>
            <TableContainer component={Paper} sx={styles.tableCard}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.th} onClick={() => handleSort('name')}>
                      <Box display="flex" alignItems="center">Name {getSortIcon('name')}</Box>
                    </TableCell>
                    <TableCell sx={styles.th}>
                      <Box display="flex" alignItems="center">Date</Box>
                    </TableCell>
                    <TableCell sx={styles.th} onClick={() => handleSort('violation')}>
                      <Box display="flex" alignItems="center">Type {getSortIcon('violation')}</Box>
                    </TableCell>
                    <TableCell sx={styles.th} style={{ minWidth: 200 }} onClick={() => handleSort('description')}>
                      <Box display="flex" alignItems="center">Description {getSortIcon('description')}</Box>
                    </TableCell>
                    <TableCell sx={styles.th} style={{ minWidth: 150 }} onClick={() => handleSort('managerReview')}>
                      <Box display="flex" alignItems="center">Manager Review {getSortIcon('managerReview')}</Box>
                    </TableCell>
                    <TableCell sx={styles.th} style={{ minWidth: 150 }} onClick={() => handleSort('reaction')}>
                      <Box display="flex" alignItems="center">Reaction {getSortIcon('reaction')}</Box>
                    </TableCell>
                    <TableCell sx={styles.th} style={{ minWidth: 160 }} onClick={() => handleSort('agreement')}>
                      <Box display="flex" alignItems="center">Agreement {getSortIcon('agreement')}</Box>
                    </TableCell>
                    <TableCell align="center" sx={styles.th} onClick={() => handleSort('sick')}>
                      <Box display="flex" alignItems="center" justifyContent="center">Sick {getSortIcon('sick')}</Box>
                    </TableCell>
                    <TableCell align="center" sx={styles.th} onClick={() => handleSort('late')}>
                      <Box display="flex" alignItems="center" justifyContent="center">Late {getSortIcon('late')}</Box>
                    </TableCell>
                    <TableCell align="center" sx={styles.th} onClick={() => handleSort('personal')}>
                      <Box display="flex" alignItems="center" justifyContent="center">Personal {getSortIcon('personal')}</Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                  ) : sortedData.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><Typography color="textSecondary">No records found for this date.</Typography></TableCell></TableRow>
                  ) : (
                    sortedData.map((row, idx) => (
                      <TableRow key={idx} sx={styles.rowHover} onClick={() => handleRowClick(row)}>
                        <TableCell sx={{ ...styles.td, fontWeight: '600' }}>{row.employee?.firstName} {row.employee?.lastName}</TableCell>
                        <TableCell sx={styles.td}>{format(selectedDate, 'dd-MM-yyyy')}</TableCell>
                        <TableCell sx={styles.td}>
                          <Chip label={getViolationConfig(row.ty_pe).label} color={getViolationConfig(row.ty_pe).color} size="small" variant="outlined" sx={{ fontWeight: 600 }} icon={getViolationConfig(row.ty_pe).icon} />
                        </TableCell>
                        <TableCell sx={styles.td}>
                          <Tooltip title={stripHtml(row.reason) || "No description"} arrow><span>{stripHtml(row.reason) || "No description"}</span></Tooltip>
                        </TableCell>
                        <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.managerReview) || '-'} arrow><span>{stripHtml(row.noteDetails?.managerReview) || '-'}</span></Tooltip></TableCell>
                        <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.employeeReaction) || '-'} arrow><span>{stripHtml(row.noteDetails?.employeeReaction) || '-'}</span></Tooltip></TableCell>
                        <TableCell sx={styles.td}><Tooltip title={stripHtml(row.noteDetails?.employeeAgreement) || '-'} arrow><span>{stripHtml(row.noteDetails?.employeeAgreement) || '-'}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={styles.td}><Chip label={row.stats?.sick || 0} sx={{ ...styles.statBadge, bgcolor: '#FEF2F2', color: '#DC2626' }} /></TableCell>
                        <TableCell align="center" sx={styles.td}><Chip label={row.stats?.late || 0} sx={{ ...styles.statBadge, bgcolor: '#FFF7ED', color: '#EA580C' }} /></TableCell>
                        <TableCell align="center" sx={styles.td}><Chip label={row.stats?.personal || 0} sx={{ ...styles.statBadge, bgcolor: '#EFF6FF', color: '#2563EB' }} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <EmployeeHistoryModal open={modalOpen} onClose={() => setModalOpen(false)} employee={selectedEmployee} />
          </>
        )}

        {/* Tab 1 — General Report */}
        {activeTab === 1 && <GeneralReport />}

        {/* Tab 2 — Records Report */}
        {activeTab === 2 && <RecordsReport />}
      </Box>
    </LocalizationProvider>
  );
};

// === BULK ABSENCE EXPORT DIALOG ===
export const BulkAbsenceExportDialog = ({ open, onClose, employees = [] }) => {
  const [exporting, setExporting] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();

  const handleExcelExport = async () => {
    if (!employees.length) return;
    setExporting(true);
    try {
      const rows = [];
      const BATCH_SIZE = 12;
      for (let i = 0; i < employees.length; i += BATCH_SIZE) {
        const batch = employees.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (emp) => {
            const empId = emp.employeeId || emp.id;
            if (!empId) return [];
            const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || empId;
            const [absences, notes] = await Promise.all([
              getEmployeeAbsences(empId, exportYear).catch(() => []),
              getEmployeeNotes(empId).catch(() => [])
            ]);
            const relevant = (absences || []).filter(a =>
              ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())
            );

            const empRows = [];
            if (relevant.length === 0 && (notes || []).length === 0) {
              empRows.push({ 'Employee': empName, 'Date': '-', 'Type': '-', 'Description': 'No records', 'Manager Review': '-', 'Reaction': '-', 'Agreement': '-' });
            }
            relevant.forEach(a => {
              empRows.push({
                'Employee': empName,
                'Date': a.startDate || a.date || '-',
                'Type': getViolationConfig(a.ty_pe).label,
                'Description': stripHtml(a.reason || a.description) || '-',
                'Manager Review': '-',
                'Reaction': '-',
                'Agreement': '-',
              });
            });
            (notes || []).forEach(n => {
              const parsed = parseNoteContent(n.content);
              empRows.push({
                'Employee': empName,
                'Date': n.incidentDate || n.createdAt || '-',
                'Type': getViolationConfig(n.violationType || (parsed.isStructured ? parsed.violationType : 'FEEDBACK_NOTE')).label,
                'Description': stripHtml(n.description || parsed.description) || '-',
                'Manager Review': stripHtml(n.managerReview || parsed.managerName) || '-',
                'Reaction': stripHtml(n.employeeReaction || parsed.employeeReaction) || '-',
                'Agreement': stripHtml(n.employeeAgreement || parsed.employeeAgreement) || '-',
              });
            });
            return empRows;
          })
        );
        rows.push(...batchResults.flat());
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bulk Absence Report');
      XLSX.writeFile(wb, `Bulk_Absence_Report_${exportYear}.xlsx`);
    } catch (e) {
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePDFExport = async () => {
    if (!employees.length) return;
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 10;
      const pw = doc.internal.pageSize.width;
      const [mBase64, aBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl('/logo1.png')
      ]);
      if (mBase64) doc.addImage(mBase64, 'PNG', margin, 5, 25, 8);
      if (aBase64) doc.addImage(aBase64, 'PNG', pw - margin - 25, 5, 25, 8);
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
      doc.text('Bulk Absence Report', pw / 2, 13, { align: 'center' });
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`Year: ${exportYear}  |  Employees: ${employees.length}`, margin, 22);

      const body = [];
      const BATCH_SIZE = 12;
      for (let i = 0; i < employees.length; i += BATCH_SIZE) {
        const batch = employees.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (emp) => {
            const empId = emp.employeeId || emp.id;
            if (!empId) return [];
            const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || empId;
            const [absences, notes] = await Promise.all([
              getEmployeeAbsences(empId, exportYear).catch(() => []),
              getEmployeeNotes(empId).catch(() => [])
            ]);
            const relevant = (absences || []).filter(a =>
              ['LATENESS', 'SICKNESS', 'PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase())
            );

            const empRows = [];
            relevant.forEach(a => empRows.push([
              empName, a.startDate || a.date || '-',
              getViolationConfig(a.ty_pe).label,
              stripHtml(a.reason || a.description) || '-', '-', '-', '-'
            ]));
            (notes || []).forEach(n => {
              const parsed = parseNoteContent(n.content);
              empRows.push([
                empName, n.incidentDate || n.createdAt || '-',
                getViolationConfig(n.violationType || (parsed.isStructured ? parsed.violationType : 'FEEDBACK_NOTE')).label,
                stripHtml(n.description || parsed.description) || '-',
                stripHtml(n.managerReview || parsed.managerName) || '-',
                stripHtml(n.employeeReaction || parsed.employeeReaction) || '-',
                stripHtml(n.employeeAgreement || parsed.employeeAgreement) || '-',
              ]);
            });
            return empRows;
          })
        );
        body.push(...batchResults.flat());
      }

      autoTable(doc, {
        startY: 27,
        head: [['Employee', 'Date', 'Type', 'Description', 'Manager Review', 'Reaction', 'Agreement']],
        body: body.length ? body : [['No data', '', '', '', '', '', '']],
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [30, 41, 59], fontSize: 6.5 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30 }, 3: { cellWidth: 45 } }
      });
      doc.save(`Bulk_Absence_Report_${exportYear}.pdf`);
    } catch (e) {
      alert('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
          Bulk Absence Export
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography variant="body2" sx={{ color: THEME.secondary }}>
          Exporting absence &amp; feedback data for <strong>{employees.length}</strong> employee{employees.length !== 1 ? 's' : ''}.
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel>Year</InputLabel>
          <Select value={exportYear} label="Year" onChange={e => setExportYear(e.target.value)}>
            <MenuItem value={currentYear}>{currentYear}</MenuItem>
            <MenuItem value={currentYear - 1}>{currentYear - 1}</MenuItem>
            <MenuItem value={currentYear - 2}>{currentYear - 2}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={exporting ? <CircularProgress size={16} /> : <TableChart />}
            onClick={handleExcelExport}
            disabled={exporting || !employees.length}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            fullWidth
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
            onClick={handlePDFExport}
            disabled={exporting || !employees.length}
            sx={{ bgcolor: THEME.primary }}
          >
            PDF
          </Button>
        </Box>

        {exporting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: THEME.secondary }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Fetching data, please wait…</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { EmployeeFeedbackReport as EmployeeFeedbackReportContent };
export default EmployeeFeedbackReport;
